import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, Typography } from '@mui/material';
import { borderRadius, boxShadows, palette, spaces } from '../../assets/styles/themes/tokens';
import { selectUser } from '../../reducers/user.selectors';
import TrialReportCardDialog from './TrialReportCardDialog';
import { isPerfectGrade } from './grade';
import { useTrialReportCardEligibility } from './useTrialReportCardEligibility';
import { useTrialReportCardProgress } from './useTrialReportCardProgress';

interface TrialReportCardCardProps {
  isCollapsed: boolean;
}

// Neutral placeholder shown in the collapsed badge before the first area is
// completed (the expanded card uses the "Nicht bewertet" copy instead).
const NEUTRAL_BADGE_INDICATOR = '–';

// Centred wrapper for the collapsed badge only.
const wrapperStyles = {
  width: '100%',
  padding: spaces.xs,
  display: 'flex',
  justifyContent: 'center',
};

// Left-aligned wrapper so the card sits flush-left in the rail (rather than
// centered), lining it up with the other sidebar sections.
const expandedWrapperStyles = {
  width: '100%',
  padding: spaces.xs,
  display: 'flex',
  justifyContent: 'flex-start',
};

// Compact A4-proportioned "paper" card, capped so it reads as a small report
// card. Content is left-aligned (was centered) to match the sidebar sections.
const expandedCardStyles = {
  width: '100%',
  maxWidth: '72px',
  aspectRatio: '210 / 297',
  borderRadius: borderRadius.xs,
  boxShadow: boxShadows.elevation1,
  backgroundColor: palette.common.white,
  border: `1.5px solid ${palette.primary.outlinedBorder}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  padding: '10px 8px 8px',
  textAlign: 'left',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: boxShadows.elevation2,
  },
};

const headlineWrapperStyles = {
  position: 'relative',
  paddingBottom: '3px',
};

const headlineStyles = {
  fontSize: '8px',
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  color: palette.text.primary,
};

const headlineUnderlineStyles = {
  position: 'absolute',
  left: '-1px',
  right: '-1px',
  bottom: 0,
  height: '2px',
  borderRadius: '2px',
  backgroundColor: palette.accent.pink,
  transform: 'rotate(-1.2deg)',
};

const subHeadlineStyles = {
  fontSize: '7px',
  marginTop: '7px',
  color: palette.text.secondary,
};

const gradeStyles = {
  fontSize: '17px',
  fontWeight: 700,
  lineHeight: 1.15,
  marginTop: '1px',
  fontVariantNumeric: 'tabular-nums',
};

// Enlarged from the old 7px (the ticket asks for a bigger ungraded label; it may
// wrap to two lines inside the narrow card). `marginTop: auto` pushes it to the
// bottom of the card, leaving the title anchored at the top.
const ungradedStyles = {
  fontSize: '12px',
  fontStyle: 'italic',
  marginTop: 'auto',
  lineHeight: 1.2,
  color: palette.text.secondary,
};

const badgeStyles = {
  width: '32px',
  height: '32px',
  borderRadius: borderRadius.xs,
  border: `1px solid ${palette.primary.outlinedBorder}`,
  backgroundColor: palette.common.white,
  boxShadow: boxShadows.elevation1,
};

const TrialReportCardCard: React.FC<TrialReportCardCardProps> = ({ isCollapsed }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const eligible = useTrialReportCardEligibility();
  const progress = useTrialReportCardProgress();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!eligible) {
    return null;
  }

  const { grade } = progress;
  // Grade is blue (the in-app accent) until every area is done, then green.
  const gradeColor = isPerfectGrade(grade) ? palette.success.main : palette.primary.main;
  const openDialog = () => setIsDialogOpen(true);

  return (
    <>
      {isCollapsed ? (
        <Box sx={wrapperStyles}>
          <ButtonBase
            onClick={openDialog}
            data-testid='trial-report-card-badge'
            aria-label={t('Report Card')}
            sx={badgeStyles}
          >
            <Typography
              variant='subtitle3'
              sx={{
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                color: grade.label ? gradeColor : palette.text.secondary,
              }}
            >
              {grade.label ?? NEUTRAL_BADGE_INDICATOR}
            </Typography>
          </ButtonBase>
        </Box>
      ) : (
        <Box sx={expandedWrapperStyles}>
          <ButtonBase onClick={openDialog} data-testid='trial-report-card' sx={expandedCardStyles}>
            <Box sx={headlineWrapperStyles}>
              <Typography sx={headlineStyles}>{t('Report Card')}</Typography>
              <Box sx={headlineUnderlineStyles} />
            </Box>
            {grade.label ? (
              <>
                <Typography sx={subHeadlineStyles}>{t('Overall Grade')}</Typography>
                <Typography sx={{ ...gradeStyles, color: gradeColor }}>{grade.label}</Typography>
              </>
            ) : (
              <Typography sx={ungradedStyles}>{t('Not graded')}</Typography>
            )}
          </ButtonBase>
        </Box>
      )}
      <TrialReportCardDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        firstName={user?.firstName}
        progress={progress}
      />
    </>
  );
};

export default TrialReportCardCard;
